<template>
  <div
    :class="[
      'wt-transcribe-segment',
      'notranslate',
      {
        '-active': isActive,
        '-single': isSingleSegment,
      },
      `-${mode}`,
    ]"
    ref="segment"
  >
    <div v-if="timeLimit" class="-segment-title" v-text="segment.title" />

    <div class="-left" v-if="mode === 'transcribeModal'">
      <div class="-timestamps">
        <span v-if="isNARRATION" class="wticon wticon-mic" />
        <span v-if="isVIDEO" class="wticon wticon-camera" />
        <span v-text="durationDisplay" />
      </div>

      <div
        v-if="isTranscriptReady"
        class="-undoredo"
        v-show="canUndo || canRedo"
      >
        <i
          title="Undo"
          class="wticon wticon-Redo2 -rotate-x"
          :class="{ '-disabled': !canUndo }"
          @click="undo"
        />
        <i
          title="Redo"
          class="wticon wticon-Redo2"
          :class="{ '-disabled': !canRedo }"
          @click="redo"
        />
      </div>
    </div>

    <div class="-container" dir="auto" ref="container">
      <div class="-highlight-animated" ref="highlightAnimated"></div>
      <div class="highlight-container" ref="highlightContainer"></div>
      <div
        class="search-highlight-container"
        ref="searchHighlightContainer"
      ></div>
      <div
        class="-edit-selection-rect"
        v-for="(rect, rectIndex) in editSelectionRects"
        :key="rectIndex"
        :style="{
          top: rect.top + 'px',
          left: rect.left + 'px',
          width: rect.width + 'px',
          height: '22px',
        }"
      />

      <div class="-text" ref="text" dir="auto" />

      <transcribe-token-editor-menu
        v-if="editOptions.isVisible"
        ref="tokenEditor"
        :key="editOptions.clickTimestamp"
        :editOptions="editOptions"
        @apply="onTokenEditorApply"
        @cancel="onTokenEditorCancel"
        @mounted="onBalloonMounted('tokenEditor')"
        @sizeChange="onBalloonMounted('tokenEditor')"
      />

      <transcribe-highlight-menu
        v-if="selectionMenuVisible"
        ref="highlightMenu"
        :selectionRect="selectionRect"
        :isFirst="isFirst"
        @edit="onClickEdit"
        @delete="onClickDelete"
        @mounted="onBalloonMounted('highlightMenu')"
      />

      <div
        v-if="(didTranscriptFail || activeTokenElements === 0) && timeLimit"
        class="-failed"
      >
        {{ 'transcribe.noAvailableTranscript' | culture }}
        <i class="wticon wticon-edit-04 wt2-color-link" />
      </div>

      <div v-if="isTranscriptPending" class="-pending">
        {{ 'transcribe.inProgress' | culture }}
      </div>
    </div>
  </div>
</template>

<script>
import { isTokenRelevantInSpan } from 'v/Story/Timeline/api/transcription';

const $getRangeSelectedNodes = function (range) {
  const start = range.startContainer.parentElement;
  const end = range.endContainer.parentElement;
  const nodes = [];
  let node;

  // walk children and siblings from start until end is found
  for (node = start; node; node = $nextNode(node)) {
    if (node._tokenData) {
      nodes.push(node);
    }
    if (node == end) {
      break;
    }
  }

  return nodes;
};

const $nextNode = function (node, previous = false) {
  if (node.firstChild) {
    return node.firstChild;
  }
  while (node) {
    if (previous ? node.previousSibling : node.nextSibling) {
      return previous ? node.previousSibling : node.nextSibling;
    }
    node = node.parentNode;
  }
};

import TranscribeGap from './TranscribeGap.vue';
import TranscribeViewOnlyGap from './TranscribeViewOnlyGap.vue';
import TranscribeInsertYourOwnGap from './TranscribeInsertYourOwnGap.vue';
import TranscribeHighlightMenu from './TranscribeHighlightMenu.vue';
import TranscribeTokenEditorMenu from './TranscribeTokenEditorMenu.vue';
import Analytics from 'c/WtAnalytics';

export default {
  name: 'wt-transcribe-segment',
  components: {
    TranscribeHighlightMenu,
    TranscribeTokenEditorMenu,
  },

  props: {
    mode: { type: String, required: false, default: 'edit' },
    segment: { type: Object, required: true },
    transcript: { type: Object, required: true },
    playbackTime: { type: [Number], required: false, default: null },
    isActive: { type: Boolean, required: true, default: false },
    containerScrollTop: { type: Number, required: true, default: 0 },
    isSingleSegment: { type: Boolean, required: true, default: false },
    guid: { type: String, required: true, default: '' },
    isFirst: { type: Boolean, required: true },
    scrollOnPlayback: { type: Boolean, required: false, default: false },
  },

  data() {
    return {
      mounted: false,
      longTextWarningDuration: 60, // in seconds
      tokenTypes: {
        GAP: 'GAP',
        WORD: 'WORD',
      },
      currentToken: null,
      tokens: [],
      tokenElements: [],
      selectionMenuVisible: false,
      selectionRect: null,
      selection: null,
      selectionRange: null,
      editRange: null,
      history: [],
      historyPosition: -1,
      childrenComponents: [],
      editOptions: {
        isVisible: false,
        rect: {},
      },
      editSelectionRects: {},
      forceWriteYourOwn: false,
      activeTokenElements: null,
      autoShowMenu: true,
      customHighlight: {
        startIndex: null,
        endIndex: null,
      },
      searchResults: [],
      currentSearchIndex: -1,
    };
  },

  computed: {
    totalSearchResults() {
      return this.searchResults.length;
    },

    inEditMode() {
      return this.mode === 'edit';
    },

    isNARRATION() {
      return app.transcribeSubType.NARRATION === this.segment.transcribeSubType;
    },

    isVIDEO() {
      return app.transcribeSubType.VIDEO === this.segment.transcribeSubType;
    },

    transcriptTokens() {
      if (!this.transcript.tokens.length) {
        return [];
      }

      return this.transcript.tokens;
    },

    transcriptStatus() {
      return this.transcript.status || this.transcript.transcriptionStatus;
    },

    isTranscriptPending() {
      return this.transcriptStatus === 'PENDING';
    },

    isTranscriptReady() {
      return (
        this.transcriptStatus === 'DONE' || this.transcriptStatus === 'UPDATED'
      );
    },

    didTranscriptFail() {
      return this.transcriptStatus === 'FAILED';
    },

    durationDisplay() {
      if (this.timeLimit) {
        const start = app.moment
          .utc(this.timeLimit?.displayStartTime * 1000)
          .format('m:ss')
          .replace(/^(?:00:)?0?/, '');
        const end = app.moment
          .utc(this.timeLimit?.displayEndTime * 1000)
          .format('m:ss')
          .replace(/^(?:00:)?0?/, '');
        return `${start} - ${end}`;
      }
      return app.moment.utc(this.segment.duration * 1000).format('m:ss');
    },

    canUndo() {
      return this.historyPosition > 0;
    },

    canRedo() {
      return this.historyPosition < this.history.length - 1;
    },

    isEditMenuVisible() {
      return this.editOptions.isVisible;
    },

    timeLimit() {
      return this.segment.timeLimit;
    },
  },

  watch: {
    currentSearchIndex() {
      this.$emit('onCurrentSearchIndexChange', this.currentSearchIndex);
    },

    isTranscriptReady() {
      if (this.isTranscriptReady) {
        this.onTranscriptReady();
      }
    },

    playbackTime() {
      if (this.isActive && this.isTranscriptReady) {
        this.setCurrentTokenByTime();
      }
    },

    isActive() {
      if (!this.isActive) {
        this.hideSelectionMenu();
        this.removeHighlightToken();
      }
    },

    currentToken: _.debounce(
      function () {
        if (this.scrollOnPlayback) {
          const highlightElement = this.$refs.highlightAnimated;
          if (highlightElement) {
            const safetyGap = 100; // pixels
            const observer = new IntersectionObserver(
              (entries) => {
                if (!entries[0].isIntersecting) {
                  const scrollOptions = {
                    behavior: 'smooth',
                    block: 'center',
                  };

                  highlightElement.scrollIntoView(scrollOptions);
                }
                observer.disconnect();
              },
              {
                root: null,
                rootMargin: `${safetyGap}px 0px ${safetyGap}px 0px`,
                threshold: 1,
              }
            );

            observer.observe(highlightElement);
          }
        }
      },
      100,
      { leading: false, trailing: true }
    ),
  },

  beforeDestroy() {
    this.destroyChildren();
    document.removeEventListener('mouseup', this.onDocumentMouseup);
    document.removeEventListener('mousedown', this.onDocumentMousedown);
    document.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('resize', this.updateHighlightOverlay);
  },

  mounted() {
    document.addEventListener('mouseup', this.onDocumentMouseup);
    document.addEventListener('mousedown', this.onDocumentMousedown);
    document.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('resize', this.updateHighlightOverlay);
    if (this.isTranscriptReady) {
      this.onTranscriptReady();
    }
    this.$nextTick(() => {
      this.mounted = true;
    });
  },

  methods: {
    setCustomHighlight(startIndex, endIndex) {
      if (!this.mounted || !this.isTranscriptReady) {
        return;
      }
      this.customHighlight.startIndex = startIndex;
      this.customHighlight.endIndex = endIndex;
      this.$nextTick(() => this.updateHighlightOverlay());
    },

    search(searchQuery) {
      this.clearSearchHighlights();
      if (!searchQuery) return;

      const words = searchQuery.toLowerCase().split(/\s+/);
      const text = this.$refs.text;
      const container = this.$refs.searchHighlightContainer;
      const containerRect = this.$refs.container.getBoundingClientRect();

      let startIndex = 0;
      let resultCount = 0;
      while (true) {
        const result = this.findNextSearchMatch(text, words, startIndex);
        if (!result) break;
        const { start, end, node } = result;
        const tokenData = node._tokenData;
        resultCount++;
        this.highlightSearchResult(
          container,
          start,
          end,
          containerRect,
          tokenData
        );
        startIndex = end;
      }
      this.navigateSearchResults(1);
      return resultCount;
    },

    findNextSearchMatch(element, words, startIndex) {
      const searchText = words.join('\\s+');
      const regex = new RegExp(searchText, 'i');
      const allText = element.textContent;
      const match = regex.exec(allText.slice(startIndex));

      if (match) {
        const matchStartIndex = startIndex + match.index;
        const matchEndIndex = matchStartIndex + match[0].length;

        return {
          element: element,
          start: matchStartIndex,
          end: matchEndIndex,
          node: this.findNodeForIndex(element, matchStartIndex),
        };
      }

      return null;
    },

    findNodeForIndex(element, targetIndex) {
      let currentIndex = 0;
      const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);

      let node;
      while ((node = walker.nextNode())) {
        if (currentIndex + node.length > targetIndex) {
          // Find the closest parent element
          let parentElement = node.parentElement;
          while (parentElement && parentElement !== element) {
            if (parentElement.nodeType === Node.ELEMENT_NODE) {
              return parentElement;
            }
            parentElement = parentElement.parentElement;
          }
          // If no parent element is found, return the original element
          return element;
        }
        currentIndex += node.length;
      }

      return null;
    },

    highlightSearchResult(container, start, end, containerRect, tokenData) {
      const range = document.createRange();
      const startNode = this.findNodeAtIndex(this.$refs.text, start);
      const endNode = this.findNodeAtIndex(this.$refs.text, end);
      if (!startNode || !endNode) return;
      range.setStart(startNode.node, startNode.offset);
      range.setEnd(endNode.node, endNode.offset);

      let rects = Array.from(range.getClientRects());
      if (rects.length === 0) return;

      // Sort rects by top position
      rects = rects.sort((a, b) => {
        if (a.top !== b.top) {
          return a.top - b.top; // Sort by top first
        } else {
          return a.x - b.x; // If top is equal, sort by x
        }
      });
      // rects = rects.sort((a, b) => a.top - b.top);

      const highlightFragment = document.createDocumentFragment();

      for (let i = 0; i <= rects.length - 1; i++) {
        const rect = rects[i];
        const highlightEl = document.createElement('div');
        highlightEl.className = 'search-highlight';
        highlightEl.style.position = 'absolute';
        highlightEl.style.backgroundColor = 'yellow';
        highlightEl.style.zIndex = '-1';
        highlightEl.style.opacity = '0.5';
        highlightEl.style.top = `${rect.top - containerRect.top}px`;
        highlightEl.style.left = `${rect.left - containerRect.left}px`;
        highlightEl.style.width = `${rect.width}px`;
        highlightEl.style.height = `${rect.height}px`;
        highlightEl.style.pointerEvents = 'none';
        highlightEl._tokenData = tokenData;

        // Add duplicated property for non-first lines
        if (i) {
          highlightEl.duplicated = true;
        }

        highlightFragment.appendChild(highlightEl);
      }

      container.appendChild(highlightFragment);
      this.searchResults.push({ start, end });
    },

    findNodeAtIndex(element, targetIndex) {
      let currentIndex = 0;
      const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);

      while (walker.nextNode()) {
        const nodeLength = walker.currentNode.length;
        if (currentIndex + nodeLength > targetIndex) {
          return {
            node: walker.currentNode,
            offset: targetIndex - currentIndex,
          };
        }
        currentIndex += nodeLength;
      }

      return null;
    },

    clearSearchHighlights() {
      const container = this.$refs.searchHighlightContainer;
      while (container.firstChild) {
        container.removeChild(container.firstChild);
      }
      this.searchResults = [];
      this.currentSearchIndex = -1;
    },

    navigateSearchResults(direction) {
      if (this.searchResults.length === 0) return;

      this.currentSearchIndex += direction;
      if (this.currentSearchIndex < 0) {
        this.currentSearchIndex = this.searchResults.length - 1;
      } else if (this.currentSearchIndex >= this.searchResults.length) {
        this.currentSearchIndex = 0;
      }

      this.scrollToCurrentSearchResult();
    },

    scrollToCurrentSearchResult() {
      if (this.currentSearchIndex === -1) return;

      const result = this.searchResults[this.currentSearchIndex];
      const { start, end } = result;

      // Find the corresponding highlight element
      const highlightElements = Array.from(
        this.$refs.searchHighlightContainer.children
      ).filter((el) => el._tokenData && el.duplicated !== true);
      const highlightElement = highlightElements[this.currentSearchIndex];

      if (highlightElement) {
        if (highlightElement._tokenData) {
          this.$emit('changeTime', highlightElement._tokenData?.startTime);
        }

        highlightElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });

        // Temporarily change the background color to make it more noticeable
        const originalColor = highlightElement.style.backgroundColor;
        highlightElement.style.backgroundColor = 'orange';
        setTimeout(() => {
          highlightElement.style.backgroundColor = originalColor;
        }, 1000);
      }
    },

    nextSearchResult() {
      this.navigateSearchResults(1);
    },

    previousSearchResult() {
      this.navigateSearchResults(-1);
    },

    updateHighlightOverlay() {
      const { startIndex, endIndex } = this.customHighlight;
      const container = this.$refs.highlightContainer;

      if (!container) {
        return;
      }
      // Clear previous highlights
      container.innerHTML = '';

      if (startIndex === null || endIndex === null) {
        return;
      }

      const containerRect = this.$refs.container.getBoundingClientRect();
      let currentRect = null;

      for (let i = startIndex; i <= endIndex; i++) {
        const element = this.tokenElements[i];
        if (!element) continue;

        const rect = element.getBoundingClientRect();
        const nextElement = this.tokenElements[i + 1];
        const nextRect = nextElement
          ? nextElement.getBoundingClientRect()
          : null;

        if (!currentRect) {
          // Start a new rectangle
          currentRect = {
            top: rect.top - containerRect.top,
            left: rect.left - containerRect.left,
            right: rect.right - containerRect.left,
            bottom: rect.bottom - containerRect.top,
          };
        } else if (rect.top === currentRect.bottom) {
          // Extend the current rectangle if on the same line
          currentRect.right = rect.right - containerRect.left;
          currentRect.bottom = rect.bottom - containerRect.top;
        } else {
          // Create a highlight for the previous rectangle and start a new one
          this.createHighlightElement(currentRect, element);
          currentRect = {
            top: rect.top - containerRect.top,
            left: rect.left - containerRect.left,
            right: rect.right - containerRect.left,
            bottom: rect.bottom - containerRect.top,
          };
        }

        // Extend to include space if there's a next element on the same line
        if (nextRect && nextRect.top === rect.top) {
          currentRect.right = nextRect.left - containerRect.left;
        }

        // If this is the last token or the next token is on a new line, create the highlight
        if (i === endIndex || (nextRect && nextRect.top > rect.top)) {
          this.createHighlightElement(currentRect, element);
          currentRect = null;
        }
      }
    },

    createHighlightElement(rect, element) {
      const highlightEl = document.createElement('div');
      highlightEl.className = 'highlight-rectangle';
      highlightEl.style.top = `${rect.top}px`;
      highlightEl.style.left = `${rect.left}px`;
      highlightEl.style.width = `${rect.right - rect.left}px`;
      highlightEl.style.height = `${rect.bottom - rect.top}px`;
      highlightEl._tokenData = element._tokenData;
      this.$refs.highlightContainer.appendChild(highlightEl);
    },
    clearCustomHighlight() {
      this.customHighlight = { startIndex: null, endIndex: null };
      this.$refs.highlightContainer.innerHTML = '';
    },
    async onClickEdit(insertEvent) {
      // todo - do.
      if (!this.selectionRange) {
        return;
      }
      this.hideSelectionMenu();
      this.editOptions.isVisible = false;

      const editRange = this.selectionRange;
      const editRect = this.selectionRect;

      const startEl = this.selectionRange.startContainer.parentElement;
      const endEl = this.selectionRange.endContainer.parentElement;

      const startTime = startEl._tokenData.startTime;
      const endTime = endEl._tokenData.endTime;
      const startIndex = startEl._tokenIndex;
      const endIndex = endEl._tokenIndex;
      const duration = endTime - startTime;
      const tokensLength = this.tokens.length;
      const originalString = this.getOriginalTimesString(startTime, endTime);

      this.setSelectionEditRects(editRange);

      let text = '';
      for (let i = startIndex; i <= endIndex; i++) {
        if (this.tokens[i].content) {
          text += (i > startIndex ? ' ' : '') + this.tokens[i].content;
        }
      }

      this.$emit('pausePlayback');
      // this.$emit('changeTime', startEl._tokenData.startTime);

      try {
        if (
          this.tokens.length > 1 &&
          duration > this.longTextWarningDuration &&
          startIndex === 0 &&
          endIndex === tokensLength - 1
        ) {
          await this.showEditLongTextWarning();
        }
        console.log(
          'Open edit token dialog, duration:',
          duration,
          'seconds. from',
          startTime,
          'to',
          endTime
        );

        const eventType = insertEvent
          ? 'VO:insertTranscript'
          : 'VO:editTranscript';
        const integerDuration = duration.toFixed(1);
        Analytics.trigger(eventType, {
          duration: integerDuration,
          ...(!insertEvent && { language: this.transcript.languageCode }),
        });

        this.setEditOptions({
          text,
          startIndex,
          endIndex,
          startTime,
          originalString,
          endTime,
          isVisible: true,
          rect: editRect,
          clickTimestamp: Date.now(),
        });
      } catch (e) {
        this.hideEditMenu();
        this.hideEditSelectionRects();
        console.log('Cancel edit entire text');
      }
    },

    showEditLongTextWarning() {
      return new Promise((resolve, reject) => {
        return app.dialog
          .confirm({
            type: app.dialog.types.CONFIRM_3,
            title: app.culture.transcribe.longTextDialogTitle,
            description: app.culture.transcribe.longTextDialogMessage,
            buttonYesText: app.culture.transcribe.longTextDialogContinue,
            buttonNoText: app.culture.generic.CANCEL,
            buttonYesAction: true,
          })
          .then((data) => {
            if (data.action) {
              resolve();
            } else {
              reject();
            }
          });
      });
    },

    showSelectionMenu() {
      if (this.selectionRect && this.inEditMode) {
        this.selectionMenuVisible = true;
      }
    },

    hideSelectionMenu() {
      this.selectionMenuVisible = false;
    },

    setSelectionEditRects(range) {
      const nodeRange = $getRangeSelectedNodes(range);
      const containerRect = this.$refs.container.getBoundingClientRect();
      this.hideEditSelectionRects();
      for (const node of nodeRange) {
        const nodeRect = node.getBoundingClientRect();
        if (this.editSelectionRects[nodeRect.top]) {
          const fixLeft =
            nodeRect.left -
            containerRect.left -
            this.editSelectionRects[nodeRect.top].left -
            this.editSelectionRects[nodeRect.top].width;
          this.editSelectionRects[nodeRect.top].width +=
            fixLeft + nodeRect.width;
        } else {
          this.editSelectionRects[nodeRect.top] = {
            width: nodeRect.width,
            left: nodeRect.left - containerRect.left,
            top: nodeRect.top - containerRect.top,
          };
        }
      }
    },

    onClickDelete(e) {
      if (!this.selectionRange) {
        console.warn('onClickDelete - no range detected');
        return;
      }

      const startEl = this.selectionRange.startContainer.parentElement;
      const endEl = this.selectionRange.endContainer.parentElement;
      const startIndex = startEl._tokenIndex;
      const endIndex = endEl._tokenIndex;

      if (!startEl._tokenData || !endEl._tokenData) {
        return;
      }

      this.hideEditMenu();
      this.hideSelectionMenu();
      this.hideEditSelectionRects();
      this.$emit('pausePlayback');
      this.$emit('changeTime', startEl._tokenData.startTime);

      const startTime = startEl._tokenData.startTime;
      const endTime = endEl._tokenData.endTime;
      const duration = endTime - startTime;
      let newTokens = _.cloneDeep(this.tokens);

      console.log(
        'Deleting',
        duration,
        'seconds. from',
        startTime,
        'to',
        endTime
      );

      newTokens.splice(startIndex, endIndex - startIndex + 1, {
        content: null,
        startTime,
        endTime,
        type: this.tokenTypes.GAP,
      });

      newTokens = this.fixCollidingGapTokens(newTokens);
      this.removeSelection();
      this.pushHistory({
        tokens: newTokens,
        time: startTime,
        isOriginal: false,
      });
      this.updateTokens({ tokens: newTokens, isOriginal: false });
    },

    onClickInsert(e) {
      if (this.mode === 'view') {
        return;
      }
      this.$emit('pausePlayback');
      this.onClickEdit('insert');
    },

    destroyChildren() {
      _.each(this.childrenComponents, (comp) => {
        comp.$destroy();
      });
      this.childrenComponents = [];
    },

    setTokensHTML() {
      this.activeTokenElements = 0;
      const container = this.$refs.text;
      container.innerHTML = '';

      this.destroyChildren();
      this.verifyEmptyTokens();

      this.tokenElements = [];
      const tokenLength = this.tokens.length;

      _.each(this.tokens, (token, tokenIndex) => {
        const isRelevant = this.timeLimit
          ? isTokenRelevantInSpan(token, this.timeLimit)
          : true;

        if (isRelevant) {
          this.activeTokenElements++;
        } else {
          if (token.startTime > this.timeLimit.endTime) {
            return false; // break;
          }
          return true; // continue;
        }

        if (tokenIndex > 0 && isRelevant) {
          const emptyTextNode = document.createTextNode(' ');
          container.appendChild(emptyTextNode);
        }

        if (token.type === this.tokenTypes.GAP) {
          const ComponentClass = app.Vue.extend(
            this.mode === 'view'
              ? TranscribeViewOnlyGap
              : tokenLength > 1
              ? TranscribeGap
              : TranscribeInsertYourOwnGap
          );
          const gapInstance = new ComponentClass({
            propsData: {
              tokenData: token,
              tokenIndex,
              isRelevant,
              mode: this.mode,
            },
          });
          gapInstance.$mount();
          const $el = gapInstance.$el;
          gapInstance.$on('click', this.onClickInsert);
          container.appendChild($el);
          this.childrenComponents.push(gapInstance);
          this.tokenElements[tokenIndex] = $el;
        } else {
          const tNode = this.createTokenElement({
            token,
            isRelevant,
            tokenIndex,
          });
          container.appendChild(tNode);
          this.tokenElements[tokenIndex] = tNode;
        }
      });
      this.setCurrentTokenByTime();
    },

    showMenuOverToken($el) {
      const $text = $el?.childNodes[0];
      let hasContent = false;
      if (this.tokens.length === 1) {
        hasContent = !_.isEmpty(this.tokens[0].conent);
      } else if (this.tokens.length > 1) {
        hasContent = true;
      }

      if (hasContent && $text && $text.nodeType === Node.TEXT_NODE) {
        // simulate text selection
        const range = document.createRange();
        range.setStart($text, 0);
        range.setEnd($text, $text.length);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
        // establish range/rect
        this.selectionRange = range;
        this.setSelectionRect();

        this.showSelectionMenu();
      }
    },

    removeHighlightToken() {
      return;
      const container = this.$refs.text;
      const highlightToken = container.querySelector('.-highlight');
      if (highlightToken) {
        const highlightTokenIndex = highlightToken._tokenIndex;
        this.tokenElements[highlightTokenIndex].classList.remove('-highlight');
      }
    },

    // highlightCurrentToken() {
    //   if (this.currentToken) {
    //     this.removeHighlightToken();
    //     const newTokenIndex = this.currentToken._tokenIndex;
    //     this.tokenElements[newTokenIndex].classList.add('-highlight');
    //   }
    // },
    highlightCurrentToken() {
      requestAnimationFrame(() => {
        const highlightEl = this.$refs.highlightAnimated;
        if (this.currentToken && this.$refs.container && highlightEl) {
          const tokenRect = this.currentToken.getBoundingClientRect();
          const containerRect = this.$refs.container.getBoundingClientRect();

          highlightEl.style.display = 'block';
          highlightEl.style.top = `${tokenRect.top - containerRect.top}px`;
          highlightEl.style.left = `${tokenRect.left - containerRect.left}px`;
          highlightEl.style.width = `${tokenRect.width}px`;
          highlightEl.style.height = `${tokenRect.height}px`;
        } else {
          if (highlightEl) {
            highlightEl.style.display = 'none';
          }
        }
      });
    },

    getTokenByTime(time) {
      let exactIndex = null;
      let closestIndex = null;

      for (let n = 0, N = this.tokenElements.length; n < N; n++) {
        const el = this.tokenElements[n];
        if (!el) {
          continue;
        }
        const tokenIndex = el._tokenIndex;
        const token = el._tokenData;
        closestIndex = tokenIndex;

        if (
          _.between(token.startTime, time, token.endTime, { includeMax: false })
        ) {
          exactIndex = tokenIndex;
          break;
        } else if (time <= token.startTime) {
          exactIndex = null;
          break;
        }
      }
      return { exactIndex, closestIndex };
    },

    getOriginalTimesString(startTime, endTime) {
      let string = '';
      const originalTokens = this.transcript.originalTokens;
      if (!originalTokens) {
        return string;
      }
      for (let i = 0; i <= originalTokens.length - 1; i++) {
        if (originalTokens[i].startTime >= startTime) {
          if (originalTokens[i].endTime <= endTime) {
            if (originalTokens[i].type === this.tokenTypes.WORD) {
              string += ` ${originalTokens[i].content}`;
            }
          } else {
            break;
          }
        }
      }
      return string;
    },

    setCurrentTokenByTime() {
      if (!this.isActive) {
        return;
      }

      const { exactIndex, closestIndex } = this.getTokenByTime(
        this.playbackTime
      );
      const tokenIndex = _.isNumber(exactIndex) ? exactIndex : closestIndex;
      this.currentToken = !_.isNull(tokenIndex)
        ? this.tokenElements[tokenIndex]
        : null;

      this.highlightCurrentToken();

      if (this.currentToken && this.$data.autoShowMenu) {
        setTimeout(() => {
          if (!this._isDestroyed) {
            this.$data.autoShowMenu = false;
            this.showMenuOverToken(this.currentToken);
          }
        }, app.TIME.second(0.4));
      }
    },

    selectByTimestamps(startTime, endTime) {
      if (!this.mounted) {
        return;
      }
      const { exactIndex: startIndex, closestIndex: startClosestIndex } =
        this.getTokenByTime(startTime);
      const { exactIndex: endIndex, closestIndex: endClosestIndex } =
        this.getTokenByTime(endTime);

      const finalStartIndex =
        startIndex !== null ? startIndex : startClosestIndex;
      const finalEndIndex = endIndex !== null ? endIndex : endClosestIndex;

      if (finalStartIndex !== null && finalEndIndex !== null) {
        this.setCustomHighlight(finalStartIndex, finalEndIndex);
      } else {
        console.warn(
          'Could not find appropriate tokens for the given time range'
        );
      }
    },

    programmaticallySelect(startIndex, endIndex) {
      const container = this.$refs.text;
      const range = document.createRange();
      const selection = window.getSelection();

      // Find the start and end elements
      const startElement = this.tokenElements[startIndex];
      const endElement = this.tokenElements[endIndex];

      if (!startElement || !endElement) {
        console.warn('Invalid start or end element');
        return;
      }

      // Set the range
      range.setStart(startElement.firstChild, 0);
      range.setEnd(endElement.firstChild, endElement.firstChild.length);

      // Apply the selection
      selection.removeAllRanges();
      selection.addRange(range);
    },

    onDocumentMouseup(e) {
      document.body.classList.remove('-no-select');
      const fromThisSegment = e.composedPath().includes(this.$refs.segment);
      if (fromThisSegment) {
        this.detectSelection(e);
      }

      if (this.isEditMenuVisible) {
        const keepEditorOpenEl =
          e.target.classList.contains('-keep-token-editor-open') ||
          e.target.closest('.-keep-token-editor-open');

        if (!keepEditorOpenEl && !this.selectionRange) {
          this.$refs.tokenEditor.apply();
        }
      } else {
        this.hideEditSelectionRects();
      }
    },

    onDocumentMousedown(e) {
      const eventPath = e.composedPath();
      const fromMenu = this.$refs.highlightMenu
        ? eventPath.includes(this.$refs.highlightMenu.$el)
        : false;
      const fromThisSegment = eventPath.includes(this.$refs.segment);

      if (this.$refs.highlightMenu && fromMenu) {
        return;
      }

      this.removeSelection();
      this.hideSelectionMenu();

      if (fromThisSegment) {
        this.$emit('setActive');
        document.body.classList.add('-no-select');
      }
    },

    onKeyDown(e) {
      if (!this.isActive) {
        return;
      }
      switch (e.keyCode) {
        case $.ui.keyCode.ESCAPE:
          if (this.isEditMenuVisible) {
            this.$refs.tokenEditor.cancel();
          } else if (this.selectionMenuVisible) {
            this.hideSelectionMenu();
            this.removeSelection();
          }
          break;
      }
    },

    detectSelection() {
      this.selection = window.getSelection();
      if (!this.selection.rangeCount) {
        return;
      }
      const selectionRange = this.selection.getRangeAt(0);

      const textContainer = this.$refs.text;
      let startNode = selectionRange.startContainer;
      let endNode = selectionRange.endContainer;

      if (
        !textContainer.contains(startNode) ||
        !textContainer.contains(endNode)
      ) {
        return;
      }

      if (startNode === endNode && _.isEmpty(startNode.textContent.trim())) {
        // probably clicked on an empty space.
        this.selection.empty();
        return;
      }

      if (_.isEmpty(startNode.textContent.trim())) {
        startNode = startNode.previousSibling?.childNodes[0];
      }
      if (_.isEmpty(endNode.textContent.trim())) {
        endNode = endNode.nextSibling?.childNodes[0];
      }

      if (!startNode || !endNode) {
        this.selection.empty();
        return;
      }

      this.selectionRange = selectionRange;

      this.selectionRange.setStart(startNode, 0);
      this.selectionRange.setEnd(endNode, endNode.length);

      if (startNode.parentElement._tokenData) {
        this.$emit('changeTime', startNode.parentElement._tokenData.startTime);
      }

      this.setSelectionRect();

      if (startNode === endNode && this.isNodeInGap(startNode)) {
        return; // clicked on a gap
      } else {
        this.showSelectionMenu();
      }

      if (startNode !== endNode) {
        this.$emit('rangeSelection', {
          startTime: app.sfp(startNode.parentElement._tokenData.startTime, 1e3),
          endTime: app.sfp(
            endNode.parentElement._tokenData.endTime - 0.001,
            1e3
          ),
        });
      }
    },

    setSelectionRect() {
      let top;
      let bottom;
      let x1;
      let x2;
      const rects = this.selectionRange.getClientRects();

      if (rects.length) {
        const containerRect = this.$refs.container.getBoundingClientRect();
        for (const rect of rects) {
          if (!bottom || rect.bottom > bottom) {
            bottom = rect.bottom;
          }
          if (top && top !== rect.top) {
            continue;
          }
          if (!x1) {
            x1 = rect.left;
          }
          x2 = rect.right;
          top = rect.top;
        }
        const avgX = x2 + (x1 - x2) / 2;
        this.selectionRect = {
          top: top - containerRect.y,
          left: avgX - containerRect.x,
          bottom: bottom - containerRect.y,
        };
      }
    },

    isNodeInGap(node) {
      return node?.parentElement.classList.contains('-gap');
    },

    removeSelection() {
      this.selectionRange = null;
      this.selection = null;

      if (window.getSelection) {
        if (window.getSelection().empty) {
          // Chrome
          window.getSelection().empty();
        } else if (window.getSelection().removeAllRanges) {
          // Firefox
          window.getSelection().removeAllRanges();
        }
      } else if (document.selection) {
        // IE?
        document.selection.empty();
      }
    },

    createTokenElement({ token, isRelevant, tokenIndex }) {
      const $el = document.createElement('span');
      if (!isRelevant) {
        $el.classList.add('-irrelevant');
      }
      $el.textContent = token.content;
      $el._tokenData = token;
      $el._tokenIndex = tokenIndex;
      return $el;
    },

    updateTokens({ tokens, isOriginal }) {
      this.$emit('saveTokens', { tokens, segmentGuid: this.guid, isOriginal });
      this.hideSelectionMenu();
      this.tokens = _.cloneDeep(tokens);
      this.setTokensHTML();
    },

    onTranscriptReady() {
      this.pushHistory({
        tokens: this.transcriptTokens,
        time: 0,
        isOriginal: this.transcript.isOriginal,
      });
      this.tokens = this.transcriptTokens;
      this.setTokensHTML();
    },

    forceUpdateSegmentFromStore() {
      this.hideSelectionMenu();
      this.hideEditMenu();
      this.hideEditSelectionRects();
      let time = 0;
      let isOriginal = false;
      const lastHistory = this.history[this.historyPosition];
      if (lastHistory) {
        time = lastHistory.time;
        isOriginal = lastHistory.isOriginal;
      }
      this.pushHistory({ tokens: this.transcriptTokens, time, isOriginal });
      this.tokens = this.transcriptTokens;
      this.setTokensHTML();
    },

    resetToOriginal() {
      const integerDuration = this.segment.duration.toFixed(1);
      Analytics.trigger('VO:resetToOriginalScript', {
        duration: integerDuration,
        language: this.transcript.languageCode,
      });
      const originalTokens = this.transcript.originalTokens;
      this.removeSelection();
      this.pushHistory({ tokens: originalTokens, time: 0, isOriginal: true });
      this.updateTokens({ tokens: originalTokens, isOriginal: true });
    },

    pushHistory({ tokens, time, isOriginal }) {
      if (this.history.length - 1 > this.historyPosition) {
        this.history.splice(
          this.historyPosition + 1,
          this.history.length - this.historyPosition + 1
        );
        this.historyPosition = this.history.length;
      } else {
        this.historyPosition++;
      }
      this.history.push({ tokens: _.cloneDeep(tokens), time, isOriginal });
    },

    undo() {
      if (this.canUndo) {
        this.hideSelectionMenu();
        this.hideEditMenu();
        this.removeHighlightToken();
        const currentItem = this.history[this.historyPosition];
        this.$emit('changeTime', currentItem.time);
        this.historyPosition--;
        const newItem = this.history[this.historyPosition];
        this.updateTokens({
          tokens: newItem.tokens,
          isOriginal: newItem.isOriginal,
        });
        this.$emit('pausePlayback');
      }
    },

    redo() {
      if (this.canRedo) {
        this.hideSelectionMenu();
        this.hideEditMenu();
        this.removeHighlightToken();
        this.historyPosition++;
        const newItem = this.history[this.historyPosition];
        this.$emit('changeTime', newItem.time);
        this.updateTokens({
          tokens: newItem.tokens,
          isOriginal: newItem.isOriginal,
        });
        this.$emit('pausePlayback');
      }
    },

    fixCollidingGapTokens(tokens) {
      const newTokens = [];
      let lastToken;
      _.each(tokens, (token) => {
        if (
          lastToken &&
          token.type === this.tokenTypes.GAP &&
          lastToken.type === this.tokenTypes.GAP
        ) {
          lastToken.endTime = token.endTime;
        } else {
          newTokens.push(token);
        }
        lastToken = token;
      });
      return newTokens;
    },

    setEditOptions(options = {}) {
      this.editOptions = Object.assign(this.editOptions, options);
    },

    hideEditMenu() {
      this.editOptions.isVisible = false;
    },

    hideEditSelectionRects() {
      this.editSelectionRects = {};
    },

    async onBalloonMounted(ref) {
      if (this.$refs[ref].isBottom) {
        return;
      }
      const segmentOffsetTop = this.$refs.segment.offsetTop;
      const offsetTop =
        segmentOffsetTop -
        this.containerScrollTop +
        this.$refs[ref].$el.offsetTop;
      const height = this.$refs[ref].$el.offsetHeight;
      const distanceFromSegmentTop = offsetTop - height;
      if (distanceFromSegmentTop < 0) {
        this.$refs[ref].setBottom?.();
        await this.$nextTick();
        const newOffsetTop =
          segmentOffsetTop -
          this.containerScrollTop +
          this.$refs[ref].$el.offsetTop;
        const newHeight = this.$refs[ref].$el.offsetHeight;
        if (newOffsetTop + newHeight < 0) {
          this.$parent.$refs.scrollArea.scrollTop =
            segmentOffsetTop + this.$refs[ref].$el.offsetTop - 30;
        }
      }
    },

    onTokenEditorApply({ text, editOptions }) {
      this.hideEditMenu();
      this.hideSelectionMenu();
      this.hideEditSelectionRects();
      let newTokens = _.cloneDeep(this.tokens);
      text = text.replace(/ +(?= )/g, ''); // remove multiple spaces from string
      text = text.replace(/\s+([.,!])/g, '$1'); // remove whitespace before all of the punctuation characters
      const { startTime, endTime, startIndex, endIndex } = editOptions;
      const duration = endTime - startTime;
      const textArr = text.split(' ');
      const textCount = textArr.length;
      const wordTime = duration / textCount;
      let lastTokenEndTime;

      console.log(
        'Replacing',
        duration,
        'seconds. from',
        startTime,
        'to',
        endTime,
        '. Token indexes: ',
        startIndex,
        '-',
        endIndex
      );

      newTokens.splice(startIndex, endIndex - startIndex + 1); // remove old tokens

      _.each(textArr, (content, tmpIndex) => {
        content = content.trim();
        const isLastToken = tmpIndex === textCount - 1;
        const tokenStartTime = lastTokenEndTime || startTime;
        lastTokenEndTime = isLastToken
          ? endTime
          : app.sfp(tokenStartTime + wordTime, 1e3);
        const isEmptyToken = _.isEmpty(content);

        newTokens.splice(startIndex + tmpIndex, 0, {
          startTime: tokenStartTime,
          endTime: lastTokenEndTime,
          content,
          // a single empty word will be replaced with a gap.
          type:
            textCount === 1 && isEmptyToken
              ? this.tokenTypes.GAP
              : this.tokenTypes.WORD,
        });
      });

      this.$emit('pausePlayback');
      this.$emit('changeTime', startTime);

      newTokens = this.fixCollidingGapTokens(newTokens);
      this.removeSelection();
      // const isOriginal = this.forceWriteYourOwn ? true : false;
      this.pushHistory({
        tokens: newTokens,
        time: startTime,
        isOriginal: false,
      });
      this.updateTokens({ tokens: newTokens, isOriginal: false });
      this.forceWriteYourOwn = false;
    },

    onTokenEditorCancel() {
      console.log('Cancel edit token dialog');
      this.hideEditMenu();
      this.hideEditSelectionRects();
    },

    onClickInsertYourOwn() {
      if (this.mode === 'view') {
        return;
      }
      this.forceWriteYourOwn = true;
      this.$emit('setActive');
      this.pushHistory({ tokens: [], time: 0, isOriginal: true });
      this.tokens = [];
      this.setTokensHTML();
    },

    verifyEmptyTokens() {
      if (!this.tokens.length && !this.timeLimit) {
        this.tokens.push({
          type: this.tokenTypes.GAP,
          startTime: 0,
          endTime: this.segment.duration,
        });
      }
    },
  },
};
</script>

<style lang="scss">
body.-no-select {
  user-select: none;
}

.wt-transcribe-segment {
  --token-highlight: rgba(6, 169, 244, 0.4);
  --token-highlight-lite: rgba(6, 169, 244, 0.2);
  --fragment-highlight: rgba(6, 169, 244, 0.2);

  .-highlight-animated {
    position: absolute;
    background-color: var(--token-highlight);
    transition: all 0.25s ease-out;
    z-index: -1;
  }

  .highlight-container {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
    z-index: -1;
  }

  .highlight-rectangle {
    position: absolute;
    background-color: var(--fragment-highlight);
    pointer-events: none;
  }

  display: flex;
  flex-direction: row;
  flex-grow: 1;
  user-select: none;
  position: relative;

  &.-edit {
    padding: 20px;
    min-height: 83px;
  }
  // overflow-y: overlay; // token edit menu can extend the height of the container

  .-segment-title {
    position: absolute;
    top: 0;
    right: 0;
    height: 16px;
    background-color: rgba(0, 0, 0, 0.75);
    color: rgb(255, 255, 255);
    font-size: 12px;
    padding: 0 10px;
    display: none;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 150px;
    z-index: 0;
  }

  &:hover {
    .-segment-title {
      display: inline-block;
    }
  }

  &.-single.-edit {
    min-height: 210px;
  }

  &.-edit {
    &:hover,
    &.-active {
      background-color: rgba(0, 0, 0, 0.05);
    }
  }

  .-left {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    height: 20px;
    color: rgba(0, 0, 0, 0.5);
    font-size: 16px;
    flex-grow: 0;
    flex-shrink: 0;
    width: 110px;

    .-timestamps {
      display: flex;
      align-items: center;

      .wticon {
        margin-right: 10px;
        &.wticon-mic {
          font-size: 16px;
        }
        &.wticon-camera {
          transform: translateY(-1px);
          font-size: 12px;
        }
      }
    }

    .-undoredo {
      width: 75px;
      display: flex;
      left: 0;
      bottom: 0;
      justify-content: space-evenly;
      align-items: flex-start;
      margin: 5px auto 0 auto;

      .-rotate-x {
        transform: rotateY(180deg);
      }

      i {
        margin: 0;
        color: $color-normal;
        font-weight: 600;
        cursor: pointer;

        &:hover {
          color: $color-black;
        }

        &.-disabled {
          color: $color-grey-2;
          pointer-events: none;
        }
      }
    }
  }

  &.-active {
    .-container {
      .-text {
        user-select: text;
      }
    }
  }

  .-container {
    flex-grow: 1;
    font-size: 16px;
    line-height: 22px;
    font-weight: 400;
    word-break: break-word;
    position: relative;

    .-text {
      user-select: none;
      padding-right: 20px; // keep - safety distance for highlight menu on the right.
      span {
        display: inline-block;
      }

      > span {
        cursor: pointer;

        &:hover {
          background-color: var(--token-highlight-lite);
        }
      }

      .-highlight {
        // background-color: var(--token-highlight);
      }

      .-irrelevant {
        display: none !important;
      }
    }

    .-failed {
      color: rgba(0, 0, 0, 0.3);
      display: flex;
      align-items: center;

      i {
        display: none;
        margin-left: 10px;
      }

      // &:hover {
      //   i {
      //     display: block;
      //   }
      // }
    }
  }

  .-edit-selection-rect {
    position: absolute;
    background-color: var(--token-highlight);
    pointer-events: none;
    z-index: -1;
  }

  &.-edit {
    .-text,
    .-failed,
    .-pending {
      padding-left: 30px;
    }
  }
}
</style>
